import React, { useState, useEffect } from 'react';
import { freeToolsAPI, paymentAPI } from '../services/api';

// Stripe Payment Links
const STRIPE_LINKS = {
  basic_pack: 'https://buy.stripe.com/00w7sL8eu6Kk0to4Pn14408',
  pro_monthly: 'https://buy.stripe.com/aFa6oH8eugkUcc6epX14407',
  founding_member: 'https://buy.stripe.com/bJe14n3Ye9Ww5NI5Tr14409'
};

/**
 * Credit Counter Badge Component
 * Shows user's credit balance and links to purchase more credits
 */
export const CreditCounter = ({ className = "" }) => {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasUnlimited, setHasUnlimited] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const data = await freeToolsAPI.getCredits();
        setIsAuthenticated(data.authenticated);
        setCredits(data.credits);
        setHasUnlimited(data.has_unlimited);
      } catch (error) {
        console.error('Failed to fetch credits:', error);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    fetchCredits();

    // Listen for credit update events
    const handleCreditUpdate = () => {
      console.log('Credit update event received, refreshing credits...');
      fetchCredits();
    };
    window.addEventListener('credit_update', handleCreditUpdate);

    return () => {
      window.removeEventListener('credit_update', handleCreditUpdate);
    };
  }, []);

  // Don't show for unauthenticated users
  if (!isAuthenticated || loading) {
    return null;
  }

  // For unlimited users, show a different badge
  if (hasUnlimited) {
    return (
      <div className={`flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200 rounded-full ${className}`}>
        <span className="text-xs font-semibold text-purple-700">
          ∞ Unlimited
        </span>
      </div>
    );
  }

  // For free users with credits
  const isLow = credits <= 1;
  const isEmpty = credits === 0;

  return (
    <a
      href={STRIPE_LINKS.basic_pack}
      className={`flex items-center px-3 py-1.5 rounded-full transition-all cursor-pointer hover:scale-105 ${
        isEmpty 
          ? 'bg-red-100 border border-red-300 hover:bg-red-200' 
          : isLow 
            ? 'bg-amber-100 border border-amber-300 hover:bg-amber-200'
            : 'bg-blue-100 border border-blue-200 hover:bg-blue-200'
      } ${className}`}
      title="Click to buy more credits"
    >
      <svg 
        className={`w-4 h-4 mr-1.5 ${
          isEmpty ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-blue-600'
        }`} 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className={`text-xs font-semibold ${
        isEmpty ? 'text-red-700' : isLow ? 'text-amber-700' : 'text-blue-700'
      }`}>
        Credits: {credits}
      </span>
      {isEmpty && (
        <span className="ml-1 text-xs text-red-600">⚠</span>
      )}
    </a>
  );
};

/**
 * Payment Modal Component
 * Shown when user runs out of credits
 */
export const PaymentModal = ({ isOpen, onClose, creditsRemaining = 0 }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {creditsRemaining === 0 ? "You're Out of Credits" : "Low on Credits"}
          </h3>
          <p className="text-gray-600">
            Each AI scan uses 1 credit. Choose how you'd like to continue:
          </p>
        </div>

        {/* Options */}
        <div className="space-y-4">
          {/* Credit Pack Option */}
          <a
            href={STRIPE_LINKS.basic_pack}
            className="block w-full p-4 bg-white border-2 border-blue-500 rounded-xl hover:bg-blue-50 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-1">
                  <span className="font-semibold text-gray-900">10 Credit Pack</span>
                  <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    Best for occasional use
                  </span>
                </div>
                <p className="text-sm text-gray-600">One-time purchase, no subscription</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-gray-900">$5</span>
                <p className="text-xs text-gray-500">$0.50 per scan</p>
              </div>
            </div>
          </a>

          {/* Pro Subscription Option */}
          <a
            href={STRIPE_LINKS.pro_monthly}
            className="block w-full p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300 rounded-xl hover:from-purple-100 hover:to-blue-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-1">
                  <span className="font-semibold text-gray-900">Pro Monthly</span>
                  <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                    Unlimited scans
                  </span>
                </div>
                <p className="text-sm text-gray-600">Unlimited AI scans + premium features</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-gray-900">$19</span>
                <p className="text-xs text-gray-500">/month</p>
              </div>
            </div>
          </a>

          {/* Lifetime Option */}
          <a
            href={STRIPE_LINKS.founding_member}
            className="block w-full p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl hover:from-amber-100 hover:to-orange-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-1">
                  <span className="font-semibold text-gray-900">Founding Member</span>
                  <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
          {/* Verify Payment Button */}
          <div className="pt-4 border-t border-gray-100">
            <button
              onClick={async () => {
                try {
                  // Assuming you pass a state setter or handle verification here
                  // Since this modal is usually simpler, we'll just call the API
                  const btn = document.getElementById('verify-btn');
                  if(btn) btn.innerText = 'Checking...';
                  
                  const result = await paymentAPI.verifyMissingPayments();
                  
                  if(btn) btn.innerText = result.message || 'Check Completed';
                  
                  // Reload page after 2s if success to show credits
                  if (result.success && result.message.includes('Found')) {
                     setTimeout(() => window.location.reload(), 2000);
                  }
                } catch (e) {
                  console.error(e);
                  alert('Error checking payments');
                }
              }}
              id="verify-btn"
              className="w-full py-2 px-4 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              I paid but don't see credits
            </button>
          </div>
                    ⭐ Best Value
                  </span>
                </div>
                <p className="text-sm text-gray-600">Lifetime access, pay once</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-gray-900">$97</span>
                <p className="text-xs text-gray-500">one-time</p>
              </div>
            </div>
          </a>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="w-full mt-6 py-3 text-gray-600 hover:text-gray-800 font-medium text-sm"
        >
          Maybe Later
        </button>
      </div>
    </div>
  );
};

export default { CreditCounter, PaymentModal };
